import { useState, useCallback, useEffect } from 'react';
import { VideoWallAlert, AlertSeverity } from '../../types/canvision.types';

// Auto-dismiss timeouts by severity (in ms)
const AUTO_DISMISS_TIMEOUTS: Record<AlertSeverity, number | null> = {
  low: 30000, // 30 seconds
  medium: 60000, // 60 seconds
  high: null, // No auto-dismiss
  critical: null, // No auto-dismiss
};

// Mock alerts for development
const MOCK_ALERTS: VideoWallAlert[] = [
  {
    id: 1,
    type: 'incident',
    severity: 'critical',
    title: 'Person Detected',
    message: 'Unauthorized person detected in restricted area',
    cameraId: 3,
    cameraName: 'Entrance Camera',
    siteId: 1,
    siteName: 'Main Office',
    createdAt: new Date(Date.now() - 5000), // 5 seconds ago
  },
  {
    id: 2,
    type: 'camera_offline',
    severity: 'high',
    title: 'Camera Offline',
    message: 'Camera connection lost',
    cameraId: 7,
    cameraName: 'Parking Lot B',
    siteId: 2,
    siteName: 'Warehouse',
    createdAt: new Date(Date.now() - 120000), // 2 minutes ago
  },
  {
    id: 3,
    type: 'motion',
    severity: 'low',
    title: 'Motion Detected',
    message: 'Movement detected after hours',
    cameraId: 12,
    cameraName: 'Back Door',
    siteId: 1,
    siteName: 'Main Office',
    createdAt: new Date(Date.now() - 300000), // 5 minutes ago
  },
];

interface UseVideoWallAlertsOptions {
  enableMockData?: boolean;
  enableAutoDismiss?: boolean;
}

interface UseVideoWallAlertsReturn {
  alerts: VideoWallAlert[];
  dismissAlert: (id: number) => void;
  markViewed: (id: number) => void;
  clearAllAlerts: () => void;
  addAlert: (alert: Omit<VideoWallAlert, 'id' | 'createdAt'>) => void;
  unviewedCount: number;
}

export function useVideoWallAlerts(
  options: UseVideoWallAlertsOptions = {}
): UseVideoWallAlertsReturn {
  const { enableMockData = false, enableAutoDismiss = true } = options;

  const [alerts, setAlerts] = useState<VideoWallAlert[]>(
    enableMockData ? MOCK_ALERTS : []
  );

  // Track auto-dismiss timers
  useEffect(() => {
    if (!enableAutoDismiss) return;

    const timers: NodeJS.Timeout[] = [];

    alerts.forEach((alert) => {
      const timeout = AUTO_DISMISS_TIMEOUTS[alert.severity];
      if (timeout !== null) {
        const elapsed = Date.now() - alert.createdAt.getTime();
        const remaining = timeout - elapsed;

        if (remaining > 0) {
          const timer = setTimeout(() => {
            setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
          }, remaining);
          timers.push(timer);
        }
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [alerts, enableAutoDismiss]);

  const dismissAlert = useCallback((id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    // TODO: Call API to dismiss alert when backend is ready
  }, []);

  const markViewed = useCallback((id: number) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === id ? { ...alert, viewed: true } : alert
      )
    );
    // TODO: Call API to mark as viewed when backend is ready
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const addAlert = useCallback(
    (alertData: Omit<VideoWallAlert, 'id' | 'createdAt'>) => {
      const newAlert: VideoWallAlert = {
        ...alertData,
        id: Date.now(), // Simple ID generation for mock
        createdAt: new Date(),
      };
      setAlerts((prev) => [newAlert, ...prev]);
    },
    []
  );

  const unviewedCount = alerts.filter((a) => !a.viewed).length;

  return {
    alerts,
    dismissAlert,
    markViewed,
    clearAllAlerts,
    addAlert,
    unviewedCount,
  };
}

export default useVideoWallAlerts;
